// caseGating.js — single source of truth for case-level access gates.
//
// v234.16.0: Consolidates the MD-agnostic Deliver gate that had been re-derived
// independently in CaseHome and DiagnoseScreen (and was missing entirely from
// CaseTimeline, the observation-checkin deep link, the Ask Orella agent, and the
// DeliverScreen itself). Every Deliver entry point and every Deliver-lock decision
// MUST derive from these helpers so the rule can never drift again.

/**
 * Does this case lack an Orella-subscribed doctor, such that the managed Deliver
 * phase (treatment & follow-up orchestration) cannot run?
 *
 * True when the case is on the external / outside-MD path (doctor_source === 'external')
 * OR its AVS was AI-generated from an uploaded outside note (diagnose source
 * 'external_note_ai'), AND no Orella doctor has since claimed it
 * (doctor_source === 'orella'). The External→Orella conversion flips doctor_source to
 * 'orella', which makes this return false and lets Deliver follow the normal flow.
 *
 * @param {object|null|undefined} c - the case object
 * @returns {boolean}
 */
export function deliverNeedsOrellaMD(c) {
  if (!c) return false
  const diagnoseSource = c.diagnoseData?.source || c.diagnose?.source || ''
  const isExternalNoteAVS = diagnoseSource === 'external_note_ai'
  const isExternalPath = c.doctorSource === 'external' || c.doctor_source === 'external'
  const hasOrellaDoctor = c.doctorSource === 'orella' || c.doctor_source === 'orella'
  return (isExternalPath || isExternalNoteAVS) && !hasOrellaDoctor
}

/**
 * Should the subscription-gate SURFACE (the "remind your doctor" disclaimer + invite
 * CTA) be shown? Only once the outside note has actually been processed into an AVS,
 * or the case is otherwise finalized — not the moment the case is shared. Before that,
 * Deliver renders as an ordinary locked card with no subscription messaging.
 *
 * @param {object|null|undefined} c
 * @param {boolean} isCompleted - case has reached a finalized/consulted state
 * @returns {boolean}
 */
export function deliverGatedSubscription(c, isCompleted) {
  if (!deliverNeedsOrellaMD(c)) return false
  const diagnoseSource = c.diagnoseData?.source || c.diagnose?.source || ''
  const isExternalNoteAVS = diagnoseSource === 'external_note_ai'
  return isExternalNoteAVS || !!isCompleted
}
